import { useState, useEffect } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";

export default function VendorDeliveries() {
  const [deliveries, setDeliveries] = useState([]);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error,setError]=useState(''),[saving,setSaving]=useState(false);

  const loadDeliveries = async () => {
    try {
      const res = await fetch("/api/vendor/deliveries", {
        credentials: "include",
      });

      const data = await res.json();

      if (data.status === "success") {
        setDeliveries(data.data || []);
      } else {
        setError(data.message||'Unable to load deliveries');
      }
    } catch (err) {
      setError('Unable to load deliveries. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDeliveries();
  }, []);

  const markDelivered = async () => {
    if(saving)return;setSaving(true);setError('');
    try {
    const res = await fetch(
      `/api/vendor/deliveries/${selectedDelivery.id}/deliver`,
      {
        method: "POST",
        credentials: "include",
      },
    );

    const data = await res.json();

    if (data.status === "success") {
      alert("Delivery completed");

      setSelectedDelivery(null);

      loadDeliveries();
    } else {
      setError(data.message||'Unable to record delivery');
    }
    }catch(e){setError('Unable to record delivery. Please try again.');}finally{setSaving(false);}
  };

  const filteredDeliveries = showAll
    ? deliveries
    : deliveries.filter((d) => d.status !== "Received");

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-10">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {error&&!selectedDelivery&&<p role="alert" className="rounded-xl bg-red-400/10 p-3 text-red-200">{error}</p>}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-serif">My Deliveries</h1>

            <p className="text-sm text-gray-400">
              Purchase Orders finalized for you
            </p>
          </div>

          <button
            onClick={() => setShowAll(!showAll)}
            className={`px-6 py-2 rounded-xl border transition ${
              showAll
                ? "bg-amber-400 text-black border-amber-400"
                : "border-white/10"
            }`}
          >
            {showAll ? "Show Pending" : "Show All"}
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {filteredDeliveries.map((d) => (
            <div
              key={d.id}
              onClick={() => setSelectedDelivery(d)}
              className="cursor-pointer rounded-2xl bg-white/5 border border-white/10 p-6 hover:border-amber-400 transition"
            >
              <div className="flex justify-between">
                <div>
                  <h3 className="text-lg font-semibold">{d.po_number}</h3>

                  <p className="text-xs text-gray-400 mt-1">{d.location}</p>
                </div>

                <StatusBadge status={d.status} />
              </div>

              <div className="mt-5 text-sm space-y-2">
                <p>
                  Expected :{" "}
                  {d.expected_date
                    ? new Date(d.expected_date).toLocaleDateString()
                    : "-"}
                </p>

                <p>Items : {d.items.length}</p>
              </div>
            </div>
          ))}
        </div>

        {!filteredDeliveries.length && (
          <div className="text-center text-gray-400 py-20">No Deliveries</div>
        )}
      </div>

      {selectedDelivery && (
        <Modal onClose={() => setSelectedDelivery(null)}>
          <h2 className="text-xl font-serif mb-6">Delivery Details</h2>
          {error&&<p role="alert" className="mb-4 rounded-xl bg-red-400/10 p-3 text-red-200">{error}</p>}

          <div className="space-y-4">
            <Row title="PO Number" value={selectedDelivery.po_number} />

            <Row title="Location" value={selectedDelivery.location} />

            <Row
              title="Expected Date"
              value={
                selectedDelivery.expected_date
                  ? new Date(
                      selectedDelivery.expected_date,
                    ).toLocaleDateString()
                  : "-"
              }
            />

            <Row title="Status" value={selectedDelivery.status} />

            <Row
              title="Delivered At"
              value={
                selectedDelivery.delivered_at
                  ? new Date(selectedDelivery.delivered_at).toLocaleString()
                  : "Pending"
              }
            />

            <div>
              <p className="text-xs text-gray-400 mb-3">Items</p>

              <div className="space-y-2">
                {selectedDelivery.items.map((i, index) => (
                  <div
                    key={index}
                    className="flex justify-between rounded-lg bg-black/30 p-3"
                  >
                    <span>{i.name}</span>

                    <span className="text-amber-400">
                      {i.qty} {i.unit}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {!['delivered','received'].includes(String(selectedDelivery.status).toLowerCase()) && (
            <div className="flex justify-end mt-8">
              <button
                onClick={markDelivered}
                disabled={saving}
                className="px-6 py-2 rounded-xl bg-amber-400 text-black"
              >
                {saving?'Saving…':'Mark Delivered'}
              </button>
            </div>
          )}
        </Modal>
      )}
    </DashboardLayout>
  );
}

function Row({ title, value }) {
  return (
    <div className="flex justify-between border-b border-white/10 pb-3">
      <span className="text-gray-400">{title}</span>

      <span>{value}</span>
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span
      className={`px-3 py-1 flex items-center justify-center rounded-full text-xs ${
        ['Received','Delivered'].includes(status)
          ? "bg-emerald-500/20 text-emerald-400"
          : "bg-amber-400/20 text-amber-400"
      }`}
    >
      {status}
    </span>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50">
      <div className="max-h-[90vh] overflow-y-auto w-full max-w-3xl rounded-2xl bg-[#0b0b0f] border border-white/10 p-8 relative">
        <button onClick={onClose} className="absolute right-4 top-4">
          ✕
        </button>

        {children}
      </div>
    </div>
  );
}
